import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import {
  type CurrencyCode,
  type CurrencyConfig,
  countryToCurrency,
  getCurrencyConfig,
} from '../lib/currency';
import { safeLocalStorage } from '../lib/safeStorage';

interface CurrencyContextValue {
  currency: CurrencyCode;
  config: CurrencyConfig;
  setCurrency: (code: CurrencyCode) => void;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

const OVERRIDE_KEY = 'currency_override';
const GEO_COUNTRY_KEY = 'geo_country';

function readStoredCurrency(): CurrencyCode | null {
  const override = safeLocalStorage.getItem(OVERRIDE_KEY);
  if (override === 'EUR' || override === 'GBP' || override === 'USD') return override;
  return null;
}

function readStoredCountry(): string | null {
  return safeLocalStorage.getItem(GEO_COUNTRY_KEY);
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    const override = readStoredCurrency();
    if (override) return override;
    const country = readStoredCountry();
    if (country) return countryToCurrency(country);
    return 'USD';
  });

  const [geoFetched, setGeoFetched] = useState(() => {
    return readStoredCurrency() !== null || readStoredCountry() !== null;
  });

  useEffect(() => {
    if (geoFetched) return;

    let cancelled = false;
    fetch('/api/geo')
      .then((res) => res.json())
      .then((data: { country: string }) => {
        if (cancelled) return;
        safeLocalStorage.setItem(GEO_COUNTRY_KEY, data.country);
        if (!readStoredCurrency()) {
          setCurrencyState(countryToCurrency(data.country));
        }
        setGeoFetched(true);
      })
      .catch(() => {
        if (!cancelled) setGeoFetched(true);
      });

    return () => { cancelled = true; };
  }, [geoFetched]);

  const setCurrency = useCallback((code: CurrencyCode) => {
    setCurrencyState(code);
    safeLocalStorage.setItem(OVERRIDE_KEY, code);
  }, []);

  const config = useMemo(() => getCurrencyConfig(currency), [currency]);

  const value = useMemo(
    () => ({ currency, config, setCurrency }),
    [currency, config, setCurrency],
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
}
